<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    ref="gc-base-layout"
    class="batch-payment"
    :tabs="getTabs"
    :selectedIndex="screenSelectedIndex"
  >
    <slot> </slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Bank Reconciliation'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="getScreenTitle"
        :secondaryLabel="undefined"
        v-if="loaded"
      />
    </template>
    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="loaded ? dataModel : {}"
          :itemSaveCallBack="saveBatchPayment"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
          :customButtons="getCustomButtons"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-header-form" v-show="!gridExpand">
        <div class="header-form">
          <div class="view-card responsive-paddings">
            <gc-form
              :formOptions="{
                gcLabelLocation: 'left',
              }"
              :formItems="getHeaderFormItems"
              :isNormal="true"
              :data-model="loaded ? dataModel : {}"
              :formMode="baseView.mode"
              :fromFrom="'gc-header-form'"
              ref="gc-header-form"
              :checkValid="isValid"
              :validationGroup="'gc-header-form'"
              :scrollingEnabled="false"
              :formHeight="135"
              :formContentReady="onHeaderFormContentReady"
              :fieldDataChanged="onFieldDataChanged"
            ></gc-form>
          </div>
        </div>
      </div>
      <!-- :formHeight="isEditMode() ? 60 : 135" -->

      <div class="gc-details-grid" v-if="loaded">
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <DxTreeList
              :data-source="getDataSource"
              :show-row-lines="true"
              :show-borders="true"
              :show-column-lines="true"
              :column-auto-width="true"
              :height="getGridHeight"
              data-structure="plain"
              :column-chooser="{
                enabled: false,
                allowSearch: true,
                mode: 'select',
                height: '100%',
                width: 300,
              }"
              ref="batch-payment-tree"
              :no-data-text="'Get started by adding parameters and generating your requirements'"
              @toolbar-preparing="onToolbarPreparing"
              @editor-preparing="onEditorPreparing"
              @saved="onSaved"
              @selection-changed="onSelectionChanged"
              @content-ready="onContentReady"
              key-expr="ID"
              parent-id-expr="Parent_ID"
              :root-value="0"
              :row-alternation-enabled="true"
              :autoExpandAll="true"
            >
              <DxEditing
                :allow-adding="false"
                :allow-updating="!isReviewMode()"
                :allow-deleting="false"
                mode="cell"
              />
              <DxSearchPanel :visible="true" />
              <DxSelection
                :recursive="true"
                :mode="'multiple'"
                ref="selectionComponent"
              />
              <!-- :mode="isReviewMode() ? 'single' : 'multiple'" -->
              <DxColumn
                data-field="Key"
                caption=""
                :fixed="true"
                :showInColumnChooser="false"
                :minWidth="230"
                :allow-editing="false"
              />

              <DxColumn
                caption="GCORP Invoice"
                :width="getColumnWidth('InvoiceRef') + 20"
                :allow-editing="false"
                data-field="GCORPInvoice"
                cellTemplate="invoiceTemplate"
              />

              <template #invoiceTemplate="{ data }">
                <div>
                  <a @click="onHyperlinkClicked(data)">{{ data.text }} </a>
                </div>
              </template>

              <DxColumn
                data-field="SupplierReference"
                :caption="'Supplier Ref'"
                :allow-editing="false"
                :minWidth="getColumnMinWidth('InvoiceRef')"
              />

              <DxColumn
                data-field="InvDate"
                data-type="Date"
                :format="'dd MMM yyyy'"
                :allow-editing="false"
                :calculate-display-value="
                  (e) => {
                    if (e.InvDate) return new Date(e.InvDate);
                    else return;
                  }
                "
                :width="getColumnWidth('Date')"
                :minWidth="getColumnMinWidth('Date')"
              />

              <DxColumn
                data-field="DueDate"
                data-type="Date"
                :format="'dd MMM yyyy'"
                :allow-editing="false"
                :calculate-display-value="
                  (e) => {
                    if (e.InvDueDate) return new Date(e.InvDueDate);
                    else return;
                  }
                "
                :width="getColumnWidth('Date')"
                :minWidth="getColumnMinWidth('Date')"
              />

              <DxColumn
                data-field="InvAmount"
                :caption="`Amount`"
                cell-template="Decimal"
                format="###,##0.00"
                :width="getColumnWidth('Amount')"
                :minWidth="getColumnMinWidth('Amount')"
                :allow-editing="false"
              />
              <DxColumn
                data-field="Balance"
                :caption="`Balance`"
                cell-template="Decimal"
                format="###,##0.00"
                :width="getColumnWidth('Amount')"
                :minWidth="getColumnMinWidth('Amount')"
                :allow-editing="false"
                :cellTemplate="
                  (data, info) => {
                    if (info.data.Items)
                      return (data.innerHTML = `<b>${info.text}</b>`);
                    else return (data.innerHTML = info.text);
                  }
                "
              />

              <DxColumn
                data-field="DiscountAmount"
                caption="Discount"
                format="##0.00"
                :show-editor-always="!isReviewMode()"
                css-class="gc-editable-field"
                :editorOptions="{
                  format: '##0.00',
                  onFocusIn: (e) => {
                    if (e.event.target.select) e.event.target.select();
                  },
                  onKeyDown: onKeyDownInDiscount,
                }"
                :allow-editing="!isReviewMode()"
                :set-cell-value="onDiscountChange"
                :width="getColumnWidth('Amount')"
                :minWidth="getColumnMinWidth('Amount')"
                edit-cell-template="discountEditor"
                :cellTemplate="
                  (data, info) => {
                    if (info.data.Items)
                      return (data.innerHTML = `<b>${info.text}</b>`);
                    else return (data.innerHTML = info.text);
                  }
                "
              />

              <template #discountEditor="{ data: cellInfo }">
                <div v-if="!cellInfo.data.Items">
                  <DxNumberBox
                    :value="cellInfo.value"
                    @value-changed="onDiscountChanged($event, cellInfo)"
                    :format="cellInfo.column.format"
                    @focus-in="
                      (e) => {
                        if (e.event.target.select) e.event.target.select();
                      }
                    "
                    @key-down="onKeyDownInDiscount($event, cellInfo)"
                    :disabled="
                      cellInfo.data.Items || !cellInfo.row.isSelected
                        ? true
                        : false
                    "
                    @initialized="onDicsountInitialized($event, cellInfo)"
                    :max="cellInfo.data.Balance > 0 ? cellInfo.data.Balance : 0"
                    :min="cellInfo.data.Balance < 0 ? cellInfo.data.Balance : 0"
                  />
                </div>
                <div v-else>
                  <span class="disabled-cell">{{ cellInfo.text }}</span>
                </div>
              </template>

              <DxColumn
                data-field="AllocatedAmount"
                caption="Allocated"
                format="###,##0.00"
                css-class="gc-editable-field"
                :show-editor-always="!isReviewMode()"
                :editorOptions="{
                  format: '###,##0.00',
                  onFocusIn: (e) => {
                    if (e.event.target.select) e.event.target.select();
                  },
                  onKeyDown: onKeyDownInAllocatedAmount,
                }"
                :set-cell-value="onAllocatedAmountChange"
                :allow-editing="!isReviewMode()"
                :width="getColumnWidth('Amount')"
                :minWidth="getColumnMinWidth('Amount')"
                edit-cell-template="allocatedEditor"
                :cellTemplate="
                  (data, info) => {
                    if (info.data.Items)
                      return (data.innerHTML = `<b>${info.text}</b>`);
                    else return (data.innerHTML = info.text);
                  }
                "
              />
              <template #allocatedEditor="{ data: cellInfo }">
                <div v-if="!cellInfo.data.Items">
                  <DxNumberBox
                    :value="cellInfo.value"
                    @value-changed="onDiscountChanged($event, cellInfo)"
                    :format="cellInfo.column.format"
                    @focus-in="
                      (e) => {
                        if (e.event.target.select) e.event.target.select();
                      }
                    "
                    @key-down="onKeyDownInAllocatedAmount($event, cellInfo)"
                    :disabled="
                      cellInfo.data.Items || !cellInfo.row.isSelected
                        ? true
                        : false
                    "
                    @initialized="onDicsountInitialized($event, cellInfo)"
                    :max="cellInfo.data.Balance > 0 ? cellInfo.data.Balance : 0"
                    :min="cellInfo.data.Balance < 0 ? cellInfo.data.Balance : 0"
                  />
                </div>
                <div v-else>
                  <span class="disabled-cell">{{ cellInfo.text }}</span>
                </div>
              </template>

              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="'###,###.00'" />
              </template>
            </DxTreeList>
          </template>
          <template #rightContent>
            <div
              class="advance-filter-panel"
              :key="'fpanel-bank-reconciliation-tree'"
            >
              <DxToolbar class="split-view-toolbar">
                <dx-item
                  location="before"
                  widget="dxTabs"
                  :options="filterPanelTabOptions"
                  selected-index="0"
                ></dx-item>

                <DxItem
                  :options="filterToolbar.clear"
                  location="after"
                  locate-in-menu="auto"
                  widget="dxButton"
                  :visible="false"
                />

                <DxItem
                  :options="filterToolbar.close"
                  location="after"
                  widget="dxButton"
                />
              </DxToolbar>
              <gc-filter-panel
                :toolbarVisible="false"
                :formItemData="filterFormItems"
                :setGridPanelFilter="setGridPanelFilter"
                :gridFoolter="showFooter"
                :showLoading="showLoading"
                :from="'stand-alone'"
                :isNormal="false"
                :key="'fp-bank-reconciliation-tree'"
                :overWriteClear="true"
              >
              </gc-filter-panel>
            </div>
          </template>
        </gc-filter-split-view>
      </div>

      <GcFooter v-if="showFooter" v-bind:class="'t-so-foolter-d'">
        <template #leftContent>
          <div
            class="gc-grid-footer-item left-content"
            v-for="item in footerLeftItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>

            <label
              v-if="item.fieldType == 'custome-string'"
              class="gc-footer-value"
              >{{ item.value }}</label
            >
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.format == 'Decimal-16-4C' ? 8 : 4"
              v-else
            />
          </div>
        </template>
        <template #rightContent>
          <div
            class="gc-grid-footer-item"
            v-for="item in footerRightItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.format == 'Decimal-16-4C' ? 8 : 6"
            />
          </div>
        </template>
      </GcFooter>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { FeatureStorage } from "../../_services/storage";
import { BatchPaymentService } from "../../_services/api/suppliers/batch-payment-service";
import DataSource from "devextreme/data/data_source";
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { mapGetters } from "vuex";
import CheckBox from "devextreme/ui/check_box";

import {
  DxTreeList,
  DxSelection,
  DxColumn,
  DxSearchPanel,
  DxEditing,
} from "devextreme-vue/tree-list";
import {
  getFactorByCode,
  getGlobalField,
  getGCORPInstanceCurrency,
} from "../../_store/helpers";
import validationEngine from "devextreme/ui/validation_engine";
import { SupplierPaymentService } from "../../_services/api/suppliers/supplier-payment.service";
import appConfig from "../../app-config";

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    DxTreeList,
    DxSelection,
    DxColumn,
    DxSearchPanel,
    DxEditing,
  },
  data() {
    return {
      loaded: false,
      generationTriggered: false,
      dataModel: {
        Status: undefined,
        DateIn: new Date(),
        DueDate: undefined,
        SupplierType: [],
        SupplierCode: [],
        Terms: undefined,
        Branch: undefined,
        Division: undefined,
        BankAccount: undefined,
        Reference: undefined,
        PaymentDate: new Date(),
        SaveAs: undefined,
        footerAllocatedAmount: 0,
        footerCheckInclude: 0,
      },
      goBackRoute: "/supplier/batch-payment-list",
      gridExpand: false,
      isValid: false,

      RunId: undefined,
      MinDate: undefined,
      MaxDate: undefined,
      AccountingPeriod: undefined,
      batchPaymentData: [],
      localDataList: [],

      showLeftContainer: true,
      showRightContainer: false,
      headerFormHeight: 0,

      showFooter: true,
      footerItems: [],

      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.closeFilterPanel();
          },
          // visible: browserHelper.tablet,
        },
        clear: {
          text: "Clear",
          onClick: () => {
            this.clearAdvanceFilters();
          },
        },
      },
      selectedRowIds: [],
      selectedGridItems: [],
      PaymentMinDate: undefined,
      PaymentMaxDate: undefined,
      selectKeys: [],
      isHeaderChanged: false,
      deselectedItems: [],
      skipDeselectSet: false,
      screenSelectedIndex: 0,
    };
  },
  async created() {
    this.$store.dispatch("defaultUserSettings/setDefaultCompanyBranchDivision");
    await this.initialized();
    // if (this.baseView.query.Status)
    //   this.dataModel.Status = this.baseView.query.Status;
    this.setFooterItems(this.localDataList);
    this.loaded = true;
  },
  methods: {
    onHyperlinkClicked(e) {
      let baseUrl = process.env.VUE_APP_BASE_URL;
      let url = `supplier-invoice/review/${e.data.GCORPInvoice}`;

      if (url) {
        if (baseUrl != "/") url = "/" + url;
        else url = baseUrl + url;
        var route = this.$router.resolve({ path: url });
        window.open(route.href, "_blank");
      }
    },
    onDiscountChanged(e, cellInfo) {
      cellInfo.setValue(e.value);
    },
    onDicsountInitialized(e, cellInfo) {
      if (cellInfo.data.Items) {
        e.element.classList.add("parent-cell-element");
      }
    },
    onSelectionChanged(e) {
      let instance = e.component.instance();
      this.selectedGridItems = instance.getSelectedRowsData("leavesOnly");

      e.currentSelectedRowKeys.forEach((element) => {
        this.onRowSelect(instance.getNodeByKey(element));
      });

      e.currentDeselectedRowKeys.forEach((element) => {
        this.onRowDeSelect(instance.getNodeByKey(element));
      });
    },
    onContentReady(e) {
      if (this.baseView.mode == "review") {
        let checkBoxList = e.element.querySelectorAll(".dx-select-checkbox");
        checkBoxList.forEach((element) => {
          var checkBoxElement = CheckBox.getInstance(element);
          if (checkBoxElement) checkBoxElement.option("disabled", true);
        });
      }
    },

    onRowSelect(node) {
      if (node && node.hasChildren) {
        node.children.forEach((element) => {
          this.onRowSelect(element);
        });
      } else {
        // let data = { ...node.data };
        if (!node) return;
        let data = node.data;
        var index = this.selectedRowIds.findIndex((x) => x == data.ID);
        if (index == -1) {
          this.selectedRowIds.push(data.ID);
        }

        let rowIndex = this.localDataList.findIndex(
          (x) => x.ID == data.Parent_ID
        );
        if (rowIndex != -1 && index == -1) {
          let temp = this.localDataList[rowIndex];
          if (data.AllocatedAmount == 0 && data.Balance != 0) {
            data.AllocatedAmount = data.Balance;
          }
          temp.Balance = (temp.Balance + data.Balance).roundNumber(2);
          temp.DiscountAmount = (
            temp.DiscountAmount + data.DiscountAmount
          ).roundNumber(2);
          temp.AllocatedAmount = (
            temp.AllocatedAmount + data.AllocatedAmount
          ).roundNumber(2);
        }
        this.setFooterItems(this.localDataList);
        this.repaintRow(node);
      }
    },
    onRowDeSelect(node) {
      if (node && node.hasChildren) {
        node.children.forEach((element) => {
          this.onRowDeSelect(element);
        });
      } else {
        // let data = { ...node.data };
        if (!node) return;
        let data = node.data;
        var index = this.selectedRowIds.findIndex((x) => x == data.ID);
        if (index != -1) {
          if (!this.skipDeselectSet) this.deselectedItems.push(data);
          this.selectedRowIds.splice(index, 1);
        }

        let rowIndex = this.localDataList.findIndex(
          (x) => x.ID == data.Parent_ID
        );
        if (rowIndex != -1 && !this.skipDeselectSet) {
          let temp = this.localDataList[rowIndex];
          temp.Balance = (temp.Balance - data.Balance).roundNumber(2);
          temp.DiscountAmount = (
            temp.DiscountAmount - data.DiscountAmount
          ).roundNumber(2);
          temp.AllocatedAmount = (
            temp.AllocatedAmount - data.AllocatedAmount
          ).roundNumber(2);

          data.DiscountAmount = 0;
          data.AllocatedAmount = 0;
        }

        this.setFooterItems(this.localDataList);
        this.repaintRow(node);
      }
    },
    repaintRow(node) {
      var treeList = this.$refs["batch-payment-tree"].instance;
      let rowIndex = treeList.getRowIndexByKey(node.key);
      if (rowIndex != -1) {
        treeList.repaintRows([rowIndex]);
      }
    },
    onSaved(e) {
      if (e) this.setFooterItems(this.localDataList);
    },
    onEditorPreparing(e) {
      if (e.parentType == "dataRow" && e.allowEditing) {
        if (e.row.node.hasChildren) {
          e.tabIndex = -1;
          e.editorOptions.disabled = true;
          e.editorOptions.readOnly = true;
          e.editorOptions.tabIndex = -1;
          e.editorElement.classList.add("dx-state-readonly");
          // e.cssClass = undefined;
          e.editorElement.addEventListener("keydown", this.handleTabKeyPress);
        } else {
          if (e.editorOptions && e.editorOptions.onKeyDown) {
            let func = e.editorOptions.onKeyDown;
            if (this.baseView.mode != "review" && e.parentType != "searchPanel")
              e.editorOptions.onKeyDown = (event) => {
                func(event, {
                  column: {
                    dataField: e.dataField,
                  },
                  data: e.row.data,
                  rowIndex: e.row.rowIndex,
                });
              };
          }
        }
      }

      // if (this.baseView.mode != "review") {
      //   if (e.editorName == "dxCheckBox") {
      //     console.log("A ", e);
      //   }
      // }

      // if (e.parentType === "dataRow" && e.row) {
      //   // e.editorOptions.readOnly = true;
      //   e.editorOptions.onValueChanged = (e) => {
      //     console.log("A ", e.event);
      //     // if (!e.event) {
      //     //   e.component.option("value", e.previousValue);
      //     //   return;
      //     // }

      //     if (e.event) e.event.preventDefault();
      //   };
      // }
    },
    handleTabKeyPress(event) {
      // Prevent tab key press event if the field is disabled
      if (event.key === "Tab") {
        event.preventDefault();
      }
    },
    onDiscountChange(newData, value, currentRowData) {
      newData.DiscountAmount = value;
      value > 0 ? (newData.DiscountOk = "Y") : (newData.DiscountOk = "N");
      newData.AllocatedAmount = currentRowData.Balance - value;

      let index = this.localDataList.findIndex(
        (x) => x.ID == currentRowData.Parent_ID
      );
      if (index != -1) {
        let temp = this.localDataList[index];
        temp.DiscountAmount = (
          temp.DiscountAmount -
          currentRowData.DiscountAmount +
          value
        ).roundNumber(2);
        temp.AllocatedAmount = (
          temp.AllocatedAmount -
          currentRowData.AllocatedAmount +
          newData.AllocatedAmount
        ).roundNumber(2);
      }

      return newData;
    },
    onAllocatedAmountChange(newData, value, currentRowData) {
      newData.AllocatedAmount = value;

      let index = this.localDataList.findIndex(
        (x) => x.ID == currentRowData.Parent_ID
      );
      if (index != -1) {
        let temp = this.localDataList[index];
        temp.AllocatedAmount = (
          temp.AllocatedAmount -
          currentRowData.AllocatedAmount +
          value
        ).roundNumber(2);
      }

      return newData;
    },
    onKeyDownInAllocatedAmount(e, cellInfo) {
      var treeList = this.$refs["batch-payment-tree"].instance;
      let node = treeList.getNodeByKey(cellInfo.data.Parent_ID);
      let rootNode = treeList.getRootNode();
      if (
        (e.event.key == "Tab" || e.event.code == "Tab") &&
        e.event.shiftKey == false
      ) {
        if (
          (node.children[node.children.length - 1].key == cellInfo.data.ID &&
            rootNode) ||
          cellInfo.data.Items
        ) {
          e.event.preventDefault();
          let nextRowKey = undefined;
          let nextRowItems = [];

          if (cellInfo.data.Items) {
            nextRowKey = cellInfo.children[0].key;
            nextRowItems = cellInfo.children;
          } else
            for (let index = 0; index < rootNode.children.length; index++) {
              if (
                rootNode.children[index].key == cellInfo.data.Parent_ID &&
                index + 1 < rootNode.children.length
              ) {
                nextRowKey = rootNode.children[index + 1].key;
                nextRowItems = rootNode.children[index + 1].children;
                break;
              }
            }

          if (nextRowKey) {
            treeList.expandRow(nextRowKey).then(() => {
              if (nextRowItems.length > 0) {
                let rowIndex = treeList.getRowIndexByKey(nextRowItems[0].key);
                if (rowIndex) {
                  setTimeout(() => {
                    treeList.editCell(rowIndex, "Discount");
                  }, 10);
                }
              }
            });
          }
        } else {
          // let rowIndex = treeList.getRowIndexByKey(nextRowItems[0].key);
          let nextKey = treeList.getKeyByRowIndex(cellInfo.rowIndex + 1);

          if (cellInfo.rowIndex && treeList.isRowSelected(nextKey) == false) {
            e.event.preventDefault();
            setTimeout(() => {
              let key = treeList.getKeyByRowIndex(cellInfo.rowIndex + 1);

              let that = this;
              function isSelected(key) {
                let rowIndex = treeList.getRowIndexByKey(key);
                if (treeList.isRowSelected(key)) {
                  let node = treeList.getNodeByKey(key);

                  if (node.children.length > 0) {
                    that.onKeyDownInAllocatedAmount(e, node);
                  } else {
                    treeList.editCell(rowIndex, "Discount");
                  }
                } else {
                  let key = treeList.getKeyByRowIndex(rowIndex + 1);
                  isSelected(key);
                }
              }

              isSelected(key);
            }, 10);
          }
        }
      }
    },
    onKeyDownInDiscount(e, cellInfo) {
      var treeList = this.$refs["batch-payment-tree"].instance;
      let node = treeList.getNodeByKey(cellInfo.data.Parent_ID);

      let rootNode = treeList.getRootNode();
      if (
        (e.event.key == "Tab" || e.event.code == "Tab") &&
        e.event.shiftKey == true
      ) {
        if (node.children[0].key == cellInfo.data.ID && rootNode) {
          e.event.preventDefault();
          let prevRowKey = undefined;
          let prevRowItems = [];

          for (let index = 0; index < rootNode.children.length; index++) {
            if (
              rootNode.children[index].key == cellInfo.data.Parent_ID &&
              index - 1 < rootNode.children.length
            ) {
              prevRowKey = rootNode.children[index - 1].key;
              prevRowItems = rootNode.children[index - 1].children;
              break;
            }
          }
          if (prevRowKey) {
            treeList.expandRow(prevRowKey).then(() => {
              if (prevRowItems.length > 0) {
                let rowIndex = treeList.getRowIndexByKey(
                  prevRowItems[prevRowItems.length - 1].key
                );
                if (rowIndex) {
                  setTimeout(() => {
                    treeList.editCell(rowIndex, "AllocatedAmount");
                  }, 10);
                }
              }
            });
          }
        }
      }
    },
    onToolbarPreparing(e) {
      // var toolbarItems = e.toolbarOptions.items;
      // var columnChooserButton = toolbarItems.find((item) => {
      //   return item.name === "columnChooserButton";
      // });
      //   var searchPanel = toolbarItems.find((item) => {
      //     return item.name === "searchPanel";
      //   });

      // e.toolbarOptions.items = toolbarItems.filter((item) => {
      //   return item.name !== "columnChooserButton";
      // });
      e.toolbarOptions.items.push({
        location: "after",
        widget: "dxButton",
        options: {
          icon: this.IsAllExpand ? "chevronup" : "chevrondown",
          text: this.IsAllExpand ? "Collapse" : "Expand",
          onClick: () => {
            this.expandOrCollapse();
          },
        },
      });
      // e.toolbarOptions.items.push(columnChooserButton);
      if (!this.isReviewMode())
        e.toolbarOptions.items.push({
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          // visible: !this.isTablet,
          options: {
            tabIndex: -1,
            icon: this.gridExpand ? "collapse" : "expand",
            onClick: () => {
              this.gridExpand = !this.gridExpand;
            },
          },
        });
      e.toolbarOptions.items.push({
        location: "after",
        locateinmenu: "auto",
        widget: "dxButton",
        options: {
          validationGroup: "formsData",
          icon: "filter",
          elementAttr: { id: "filter-btn" },
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
            var item = document.getElementById("filter-btn");
            this.$logger.debug("item ", item);
            item.classList.add("dx-state-invisible");

            setTimeout(() => {
              this.reSizeTreeList();
            }, 50);
          },
          visible: !this.showRightContainer,
        },
      });
    },
    reSizeTreeList() {
      var treeList = this.$refs["batch-payment-tree"].instance;

      treeList.beginUpdate();
      treeList.updateDimensions();
      treeList.endUpdate();
    },
    expandOrCollapse() {
      var treeList = this.$refs["batch-payment-tree"].instance;
      treeList.beginUpdate();
      treeList.forEachNode((node) => {
        if (!this.IsAllExpand) treeList.expandRow(node.key);
        else treeList.collapseRow(node.key);
      });
      treeList.endUpdate();
      this.IsAllExpand = !this.IsAllExpand;
      setTimeout(() => {
        treeList.repaint();
      }, 50);
    },
    getColumnMinWidth(Column) {
      return ColumnWidth.getValue(Column).minWidth;
    },
    getColumnWidth(Column) {
      return ColumnWidth.getValue(Column).width;
    },
    async initialized() {
      this.showLoading(true);
      if (this.isCreateMode()) {
        await this.$store.dispatch("screenFactors/getFactors", [91]);
        this.setDateInValidation();
        this.dataModel.BankAccount = `${getFactorByCode(91).FactorInt}`;
        const { data: apFactorDetail } =
          await BatchPaymentService.getAPFactor();
        let activeAccountingPeriod =
          apFactorDetail.value[0].AccountingPeriodCode;
        const { data: activeAccountingPeriodDetail } =
          await BatchPaymentService.getCurrentAccountingPeriod(
            activeAccountingPeriod
          );
        this.AccountingPeriod = activeAccountingPeriod;
        this.ActiveMonth = activeAccountingPeriodDetail.value[0].Month;
        this.ActiveYear = activeAccountingPeriodDetail.value[0].Year;
        this.MinDate = activeAccountingPeriodDetail.value[0].DateStart;
        let { data: dateEndDetail } =
          await BatchPaymentService.getCurrentAccountingPeriodEndDate();

        this.MaxDate = dateEndDetail.value[0].DateEnd;
      } else {
        this.IsAllExpand = true;
        await BatchPaymentService.getBatchPaymentRun(this.baseView.id).then(
          (res) => {
            let data = res.data.value[0];
            this.dataModel.DateIn = new Date(data.TransactionDate);
            this.dataModel.BankAccount = data.BankAccountCode.toString();
            this.dataModel.PaymentDate = new Date(data.PaymentDate);
            this.dataModel.Reference = data.Reference;
            this.dataModel.DueDate = new Date(data.DueBeforeDate);
            this.dataModel.SupplierType =
              data.BatchPaymentSupplierTypeFilters.map(
                (x) => x.SupplierTypeCode
              );
            this.dataModel.SupplierCode = data.BatchPaymentSupplierFilters.map(
              (x) => x.SupplierCode
            );
            if (data.Status == "A") {
              this.dataModel.Status = "O";
            } else {
              this.dataModel.Status = data.Status;
            }
          }
        );

        this.selectKeys = [];

        let BatchPaymentSupplier = [];
        let BatchPaymentInvoiceLine = [];
        await BatchPaymentService.getBatchPaymentSupplier(
          this.baseView.id
        ).then((res) => {
          BatchPaymentSupplier = res.data.value;
        });
        await BatchPaymentService.getBatchPaymentInvoiceLine(
          this.baseView.id
        ).then((res) => {
          BatchPaymentInvoiceLine = res.data.value;
        });

        let groupedData = [];
        var indexID = 1;
        BatchPaymentSupplier.forEach((element) => {
          element.ID = indexID++;
          element.Items = [];
          let Balance = 0;
          let DiscountAmount = 0;
          let AllocatedAmount = 0;
          let SupplierName = undefined;
          BatchPaymentInvoiceLine.forEach((item) => {
            if (item.SupplierCode == element.SupplierCode) {
              item.ID = indexID++;
              item.Parent_ID = element.ID;
              item.DiscountAmount = item.Discount;
              item.GCORPInvoice = item.GCORPInvoice.toString();
              // Balance = (Balance + item.Balance).roundNumber(2);
              item.disabled = true;
              element.Items.push(item);
              SupplierName = item.SupplierName;
              delete item.Key;

              if (item.LineOk == "Y") {
                this.selectKeys.push(item.ID);
                // if (this.isReviewMode()) {
                //   DiscountAmount += item.DiscountAmount;
                //   AllocatedAmount += item.AllocatedAmount;
                // }
              } else {
                // if (this.isReviewMode()) {
                //   DiscountAmount += item.DiscountAmount;
                //   AllocatedAmount += item.AllocatedAmount;
                // }
                item.AllocatedAmount = 0;
              }
              groupedData.push(item);
            }
          });

          groupedData.push({
            Parent_ID: 0,
            ID: element.ID,
            Key: SupplierName,
            SupplierCode: element.SupplierCode,
            Balance: Balance,
            DiscountAmount: DiscountAmount,
            AllocatedAmount: AllocatedAmount,
            Items: element.Items,
          });
        });
        this.localDataList = groupedData;
        this.setFooterItems(this.localDataList);

        this.showLoading(false);

        setTimeout(() => {
          var treeList = this.$refs["batch-payment-tree"].instance;
          if (treeList) treeList.selectRows(this.selectKeys, true);
        }, 250);
      }
      this.showLoading(false);

      this.setFooterItems(this.localDataList);

      this.loaded = true;
    },
    async saveBatchPayment(SaveAs) {
      this.dataModel.SaveAs = SaveAs;

      let runID = this.baseView.id ? this.baseView.id : 0;
      let headerform = await validationEngine.validateGroup("gc-header-form");
      if (!headerform.isValid)
        this.displayValidationSummary(headerform.brokenRules);
      else {
        if (this.isHeaderChanged) {
          let confirmSave = await this.$confirm(
            "Generate criteria has been changed, continue without re-generating ?",
            "Generate Records"
          );

          if (!confirmSave) return;
        }

        var count = this.selectedGridItems.length;
        var payload = {
          TransactionDate: this.dataModel.DateIn.toSaveDate(),
          PaymentDate: this.dataModel.DateIn.toSaveDate(),
          BankAccountCode: this.dataModel.BankAccount,
          Reference: this.dataModel.Reference,
          DueBeforeDate: this.dataModel.DueDate.toSaveDate(),
          Action: SaveAs,
          Status: SaveAs,
          PaymentMethod: "EFT",
          RunId: runID,
          BatchPaymentSupplierFilters: this.dataModel.SupplierCode.map((x) => {
            return {
              RunId: runID,
              SupplierCode: x,
            };
          }),
          BatchPaymentSupplierTypeFilters: this.dataModel.SupplierType.map(
            (x) => {
              return {
                RunId: runID,
                SupplierTypeCode: x,
              };
            }
          ),
          BatchPaymentSuppliers: [],
        };
        if (count === 0) {
          this.$alert(
            "You have not selected an Invoices. Please include at least 1 Invoice before attempting to create a Batch Payment Run.",
            "Warning"
          );
          return;
        }

        var parentItems = this.localDataList.filter((x) => x.Parent_ID == 0);

        parentItems.forEach((element) => {
          let obj = {
            RunId: runID,
            SupplierCode: element.SupplierCode,
            SupplierOk: "",
            Discount: element.DiscountAmount,
            BatchPaymentInvoiceLines: [],
          };

          element.Items.forEach((item) => {
            obj.BatchPaymentInvoiceLines.push({
              AllocatedAmount: item.AllocatedAmount,
              Balance: item.Balance,
              DiscountAmount: item.DiscountAmount,
              DiscountOk: item.DiscountOk,
              GCORPInvoice: item.GCORPInvoice,
              InvAmount: item.InvAmount,
              InvDate: item.InvDate,
              InvDueDate: item.InvDueDate,
              LineNo: item.LineNo,
              LineOk:
                this.selectedGridItems.findIndex((x) => x.ID == item.ID) != -1
                  ? "Y"
                  : "N",
              SupplierCode: item.SupplierCode,
              SupplierReference: item.SupplierReference,
              APDataXref: item.APDataXref,
            });
          });

          let count = obj.BatchPaymentInvoiceLines.filter(
            (x) => x.LineOk == "Y"
          );
          obj.SupplierOk = count.length > 0 ? "Y" : "N";

          payload.BatchPaymentSuppliers.push(obj);
        });

        // save as draft
        if (this.dataModel.SaveAs === "A") {
          this.showLoading(true);
          await BatchPaymentService.saveAsDraft(payload)
            .then((res) => {
              if (res) this.showLoading(false);
              this.$notify(`Batch payment saved successfully`, "success");
              this.setIgnoreRouteChangeConfirmation();
              this.$router.push({
                path: `/supplier/batch-payment-list`,
              });
            })
            .catch((err) => {
              this.showLoading(false);
              this.displayServerError(err, "Error");
            });
        } else {
          // save and commit
          this.showLoading(true);
          await BatchPaymentService.Commit(payload)
            .then((res) => {
              if (res) this.showLoading(false);
              this.$notify(`Batch payment Commited successfully`, "success");
              this.setIgnoreRouteChangeConfirmation();
              this.$router.push({
                path: `/supplier/batch-payment-list`,
              });
            })
            .catch((err) => {
              this.showLoading(false);
              this.displayServerError(err, "Save Failed");
            });
        }
      }
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    onHeaderFormContentReady(e) {
      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;
      setTimeout(() => {
        var headerToolBarForm = e.component;
        var dataField = this.isCreateMode()
          ? "DueDate"
          : this.isEditMode()
          ? "Branch"
          : undefined;
        if (
          dataField &&
          headerToolBarForm &&
          headerToolBarForm.getEditor(dataField)
        )
          headerToolBarForm.getEditor(dataField).instance().focus();
      }, 500);
    },
    onFieldDataChanged(e) {
      if (
        e.value &&
        e.dataField != "Reference" &&
        e.dataField != "BankAccount"
      ) {
        this.isHeaderChanged = true;
      }
    },
    isReviewMode() {
      return this.baseView.mode == "review";
    },
    isCreateMode() {
      return this.baseView.mode == "create";
    },
    isEditMode() {
      return this.baseView.mode == "edit";
    },
    getCreateModeSaveOptions() {
      return [
        { text: "Commit", id: "C" },
        { text: "Save as Draft", id: "D" },
      ];
    },
    getEditModeSaveOptions() {
      return [{ text: "Commit", id: "C" }];
    },
    getReviewModeSaveOptions() {
      return [{ text: "Cancel", id: "X" }];
    },
    getReviewModeFields() {
      return [];
    },
    getEditModeFields() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField: "Branch",
                  dataField: "Branch",
                  label: {
                    text: "Branch",
                  },
                  required: true,
                },
                {
                  screenField: "Division",
                  dataField: "Division",
                  label: {
                    text: "Division",
                  },
                  required: true,
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField: "BankAccount",
                  dataField: "BankAccount",
                  label: {
                    text: "Bank Account",
                  },
                  required: true,
                },
              ],
            },
          ],
        },
      ];
    },
    getCreateModeFields() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField: "DateIn",
                  dataField: "DateIn",
                  editorOptions: {
                    min: this.PaymentMinDate,
                    max: this.PaymentMaxDate,
                    allowUpdate: this.baseView.mode != "review",
                    onValueChanged: (e) => {
                      if (
                        e.value < this.PaymentMinDate ||
                        e.value > this.PaymentMaxDate
                      ) {
                        e.component.beginUpdate();
                        e.component.option("value", e.previousValue);
                        e.component.endUpdate();
                      } else {
                        if (this.localDataList.length > 0)
                          this.onDateInValueChange(e);
                      }
                    },
                  },
                  required: true,
                },
                {
                  screenField: "BankAccount",
                  dataField: "BankAccount",
                  label: {
                    text: "Bank Account",
                  },
                  required: true,
                  dataSourceConfig: {
                    endpoint: "api/BankRecord",
                    sort: {
                      selector: "AccountName",
                      asc: true,
                    },
                    filter:
                      this.baseView.mode == "review"
                        ? undefined
                        : [
                            ["CurrencyCode", "=", getGCORPInstanceCurrency()],
                            "and",
                            ["Inactive", "=", "N"],
                          ],
                  },
                  validationRules: [
                    {
                      type: "required",
                      message:
                        "Please select Bank Account before attempting to save this payment",
                    },
                  ],
                },
                {
                  screenField: "Reference",
                  dataField: "Reference",
                  editorOptions: {
                    maxLength: 100,
                  },
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField: "DueDate",
                  dataField: "DueDate",
                  editorOptions: {
                    allowUpdate: this.allowPaymentRunGeneration,
                  },
                  label: {
                    text: "Due Before",
                  },
                  required: true,
                },
                {
                  screenField: "SupplierType",
                  dataField: "SupplierType",
                  label: {
                    text: "Supplier Type",
                  },
                  editorOptions: {
                    showClearButton: true,
                    showSelectionControls: true,
                    select: ["Inactive", "Code", "Description"],
                    allowUpdate: this.allowPaymentRunGeneration,
                  },
                  required: false,
                  editorType: "dxTagBox",
                },
                {
                  screenField: "ApremitCreateSuppliers",
                  dataField: "SupplierCode",
                  label: {
                    text: "Supplier",
                  },
                  editorOptions: {
                    showClearButton: true,
                    showSelectionControls: true,
                    select: ["AutoCHQ", "SupplierCode", "SupplierName"],
                    allowUpdate: this.allowPaymentRunGeneration,
                    selectAllMode: "allPages",
                  },
                  required: false,
                  editorType: "dxTagBox",
                },
                {
                  dataField: "Generate",
                  label: {
                    text: " ",
                    visible: false,
                  },
                  editorType: "dxButton",
                  cssClass: "btn-generate",
                  editorOptions: {
                    validationGroup: "gc-header-form",
                    onClick: async () => {
                      this.onGenerateClick();
                    },
                    text: "Generate",
                    type: "default",
                    rtlEnabled: false,
                    disabled: true,
                    allowUpdate:
                      this.allowPaymentRunGeneration &&
                      this.dataModel.DueDate !== undefined,
                  },
                },
              ],
            },
          ],
        },
      ];
    },
    async onGenerateClick(skip) {
      if (this.localDataList.length > 0 && skip != true) {
        let confirmGenerateClick = await this.$confirm(
          "This will result in losing your unsaved Batch Payment. Are you sure you want to regenerate the data?",
          "Unsaved Changes"
        );
        if (!confirmGenerateClick) return;
        else {
          this.deselectedItems = [];
          this.selectAllRows(false);
        }
      }

      let RunId = 0;
      if (!this.isCreateMode()) {
        RunId = this.baseView.id;
      }

      this.generationTriggered = true;

      this.showLoading(true);
      let filteData = {
        TransactionDate: this.dataModel.DateIn.toSaveDate(),
        PaymentDate: this.dataModel.DateIn.toSaveDate(),
        BankAccountCode: this.dataModel.BankAccount,
        Reference: this.dataModel.Reference,
        DueBeforeDate: this.dataModel.DueDate.toSaveDate(),
        Action: "N",
        PaymentMethod: "EFT",
        RunId: RunId,
        BatchPaymentSupplierFilters: this.dataModel.SupplierCode.map((x) => {
          return {
            RunId: RunId,
            SupplierCode: x,
          };
        }),
        BatchPaymentSupplierTypeFilters: this.dataModel.SupplierType.map(
          (x) => {
            return {
              RunId: RunId,
              SupplierTypeCode: x,
            };
          }
        ),
      };

      try {
        let local = [...this.localDataList];
        if (skip) {
          this.skipDeselectSet = true;
          await this.selectAllRows(false);
        }
        await BatchPaymentService.getGenerateData(filteData)
          .then(async (res) => {
            this.isHeaderChanged = false;
            let groupedData = [];
            var indexID = 1;
            let selectableIDs = [];
            res.data.BatchPaymentSuppliers.forEach(async (element) => {
              element.ID = indexID++;
              element.Items = [];

              let Balance = 0;
              let DiscountAmount = 0;
              let AllocatedAmount = 0;
              element.BatchPaymentInvoiceLines.forEach((item) => {
                var filterItem = local.filter(
                  (x) =>
                    x.APDataXref == item.APDataXref &&
                    x.GCORPInvoice == item.GCORPInvoice &&
                    x.SupplierCode == item.SupplierCode &&
                    x.Balance == item.Balance
                );

                if (filterItem.length > 0) {
                  item.DiscountAmount = filterItem[0].DiscountAmount;
                  item.AllocatedAmount = filterItem[0].AllocatedAmount;
                }

                item.ID = indexID++;
                item.Parent_ID = element.ID;
                groupedData.push(item);
                element.Items.push(item);

                let isDeselected = this.deselectedItems.findIndex(
                  (x) =>
                    x.APDataXref == item.APDataXref &&
                    x.GCORPInvoice == item.GCORPInvoice &&
                    x.SupplierCode == item.SupplierCode &&
                    x.Balance == item.Balance
                );

                if (isDeselected == -1) {
                  selectableIDs.push(item.ID);
                }
              });

              groupedData.push({
                Parent_ID: 0,
                ID: element.ID,
                Key: element.SupplierName,
                SupplierCode: element.SupplierCode,
                Balance: Balance,
                DiscountAmount: DiscountAmount,
                AllocatedAmount: AllocatedAmount,
                Items: element.Items,
              });
            });

            this.localDataList = groupedData;
            this.setFooterItems(this.localDataList);

            this.showLoading(false);
            this.isSaveAsDraftProcessed = false;
            var treeList = this.$refs["batch-payment-tree"].instance;

            await treeList.forEachNode((node) => {
              treeList.expandRow(node.key);
            });
            this.IsAllExpand = true;

            treeList.selectRows(selectableIDs, true).then(() => {
              // treeList.forEachNode((node) => {
              //   treeList.collapseRow(node.key);
              // });
              this.skipDeselectSet = false;

              // var treeList = this.$refs["batch-payment-tree"].instance;
              if (treeList) treeList.repaint();
            });
          })
          .catch((err) => {
            if (err) {
              this.setFooterItems(this.localDataList);

              this.showLoading(false);
            }
          });
      } catch ({ data }) {
        this.showLoading(false);
        this.displayServerError(data, "Error");
      }

      // setTimeout(() => {
      //   this.selectAllRows();
      // }, 100);
    },
    selectAllRows(value) {
      var treeList = this.$refs["batch-payment-tree"].instance;
      if (value == false) treeList.deselectAll();
      else treeList.selectAll();
    },
    batchPaymentDetailPostProcess(data) {
      let tempArray = [];

      var indexID = 1;
      data.forEach((element) => {
        element.Parent_ID = 0;
        element.ID = indexID++;

        delete element.SupplierCode;
        element.AllocatedAmount = 0;

        element.Items.forEach((item) => {
          item.ID = indexID++;
          item.Parent_ID = element.ID;
          item.InvDate = new Date(item.InvDate);
          delete item.Key;
          element.AllocatedAmount += item.AllocatedAmount;
          tempArray.push(item);
        });
        tempArray.push(element);
      });

      return tempArray;
    },

    getTotalAllocatedAmount() {
      var total = 0;
      this.selectedGridItems.forEach((element) => {
        if (element.AllocatedAmount) total = total + element.AllocatedAmount;
      });
      return total;
    },
    getCountSelectedInvoices() {
      if (this.baseView.mode == "review")
        return this.selectedGridItems.filter((x) => x.LineOk == "Y").length;
      else return this.selectedRowIds.length;
    },
    // Footer
    async setFooterItems(gridItems) {
      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.dataModel, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.forEach((x) => {
          if (x.CheckInclude || this.baseView.mode == "review")
            result += parseFloat(x[dataField]).roundNumber(4);
          else if (x.CheckInclude == undefined)
            result += parseFloat(x[dataField]).roundNumber(4);
        });

      return result;
    },
    // Filter Panel
    closeFilterPanel() {
      this.showRightContainer = !this.showRightContainer;

      var item = document.getElementById("filter-btn");
      item.classList.remove("dx-state-invisible");

      setTimeout(() => {
        this.reSizeTreeList();
      }, 50);
    },
    setGridPanelFilter(filters, valueChanged) {
      let dataTreeList = this.$refs["batch-payment-tree"].instance;

      if (valueChanged != undefined || filters.length > 0) {
        if (filters.length > 0) dataTreeList.filter(filters);
        else if (filters.length == 0) dataTreeList.clearFilter();

        dataTreeList.refresh(true);
      }
      this.showLoading(false);
    },
    async setDateInValidation() {
      var currentPaymentDate = undefined;
      if (this.baseView.mode == "review")
        currentPaymentDate = new Date(this.dataModel.DateIn.replace("Z", ""));
      else currentPaymentDate = this.PaymentMinDate;

      SupplierPaymentService.getActiveAccountingPeriodStartDate()
        .then((startDateResult) => {
          if (startDateResult) {
            let accountingPeriodStartDate = new Date(
              startDateResult.data.value[0].StartDate.replace("Z", "")
            );

            if (
              this.baseView.mode == "review" &&
              currentPaymentDate.getTime() > accountingPeriodStartDate.getTime()
            ) {
              this.PaymentMinDate = currentPaymentDate;
            } else {
              this.PaymentMinDate = accountingPeriodStartDate;
            }
          } else {
            this.PaymentMinDate = currentPaymentDate;
          }
        })
        .catch(() => {
          this.PaymentMinDate = currentPaymentDate;
        });

      SupplierPaymentService.getActiveAccountingPeriodEndDate().then(
        (endDateResult) => {
          if (endDateResult) {
            let accountingPeriodEndDate = endDateResult.data.value[0].EndDate;
            this.PaymentMaxDate = new Date(
              accountingPeriodEndDate.replace("Z", "")
            );
          }
        }
      );
    },
    editCallBack() {
      this.refreshTreeList();
    },
    refreshTreeList() {
      var treeList = this.$refs["batch-payment-tree"].instance;
      if (treeList) {
        treeList.refresh(true).then(() => {
          treeList.selectRows(this.selectKeys, true);
        });
      }
    },

    async onDateInValueChange(e) {
      if (!e.event) return;

      if (e.value < e.previousValue) {
        let confirmChange = await this.$confirm(
          `Changing the Batch Payment Date to ${e.value.toShortDateString()} will automatically remove any invoice that was created on ${new Date(
            e.value
          )
            .addDays(1)
            .toShortDateString()} or later. Do you wish to proceed?`,
          "Generate Records"
        );

        this.onUpdateDateValue(e, confirmChange);
      } else {
        let confirmChange = await this.$confirm(
          `Changing the Batch Payment Date to ${e.value.toShortDateString()} will automatically include any invoice that was created up to and including  ${e.value.toShortDateString()}. Do you wish to proceed?`,
          "Generate Records"
        );

        this.onUpdateDateValue(e, confirmChange);
      }
    },
    onUpdateDateValue(e, nextAction) {
      if (nextAction) {
        this.onGenerateClick(true);
      } else {
        e.component.beginUpdate();
        e.component.option("value", e.previousValue);
        e.component.endUpdate();
      }
    },
    async resetBatchPayment() {
      let confirmReset = await this.$confirm(
        "You have unsaved changes. Are you sure you want to reset the screen ?",
        "Unsaved changes"
      );

      if (confirmReset) {
        let formInstance =
          this.$refs["gc-header-form"].$refs["gc-form-comp"].instance;
        if (formInstance) {
          this.IsAllExpand = false;
          formInstance.resetValues();
          this.dataModel.BankAccount = `${getFactorByCode(91).FactorInt}`;
          this.generationTriggered = false;
          this.localDataList = [];
        }
      }
    },
    async cancelBatchPayment() {
      let confirmCancel = await this.$confirm(
        "Are you sure you want to Cancel ?",
        "Cancel Payments"
      );
      if (confirmCancel) {
        this.showLoading(true);
        let payload = {
          RunId: this.baseView.id,
          Action: "X",
        };
        BatchPaymentService.cancel(payload)
          .then((res) => {
            this.showLoading(false);
            if (res) {
              this.$notify(`Batch payment canceled successfully`, "success");
              this.setIgnoreRouteChangeConfirmation();
              this.$router.push({
                path: `/supplier/batch-payment-list`,
              });
            }
          })
          .catch((err) => {
            this.showLoading(false);
            this.displayServerError(err, "Cancellation Faild");
          });
      }
    },

    downloadABAFile() {
      let RunId = this.baseView.id;

      var blobUrl = `${appConfig.VUE_APP_ROOT_API}/api/BatchPaymentRun/${RunId}/Download`;
      Object.assign(document.createElement("a"), {
        target: "_blank",
        href: blobUrl,
      }).click();
    },
  },
  computed: {
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    allowPaymentRunGeneration() {
      return this.baseView.mode == "create" && !this.generationTriggered;
    },
    getCustomButtons() {
      if (
        // this.baseView.query.Status == "Open" ||
        this.baseView.mode != "review"
      ) {
        let saveOptions = [
          {
            text: "Draft",
            onClick: () => {
              this.saveBatchPayment("A");
            },
          },
          {
            text: "Commit",
            onClick: () => {
              this.saveBatchPayment("C");
            },
          },
        ];
        if (this.baseView.mode == "create")
          saveOptions.unshift({
            text: "Reset",
            onClick: () => {
              this.resetBatchPayment();
            },
          });
        else {
          saveOptions.unshift({
            text: "Cancel",
            onClick: () => {
              this.cancelBatchPayment();
            },
          });
        }
        return saveOptions;
      } else
        return [
          {
            icon: "fa-solid fa-file-arrow-down",
            disabled: this.dataModel.Status != "C" || this.isReadOnlyUser,
            visible: FeatureStorage.checkPermission(
              "BatchPaymentCommit",
              "create"
            ),
            onClick: () => {
              this.downloadABAFile();
            },
          },
        ];
    },
    isReadOnlyUser() {
      let isReadOnly = false;
      let matchedItem = FeatureStorage.getItems().filter(
        (x) =>
          x.ApplicationFeatureCode.trim() == "BatchPaymentCommit" &&
          (x.Type == "Screen" ||
            x.Type == "Settings Screen" ||
            x.Type == "Screen Setting" ||
            x.Type == "Grid Report" ||
            x.Type == "Transaction Report" ||
            x.Type == "Formatted Report")
      );
      if (
        matchedItem.length > 0 &&
        matchedItem[0].WritePermission != "Y" &&
        matchedItem[0].ReadPermission == "Y"
      )
        isReadOnly = true;

      return isReadOnly;
    },
    getGridHeight() {
      return this.gridHeight - 75 - this.headerFormHeight - 40;
    },
    getDataSource() {
      return new DataSource({
        store: new ArrayStore({
          key: "ID",
          data: this.localDataList,
        }),
        reshapeOnPush: true,
      });
    },
    getHeaderFormItems() {
      return this.getCreateModeFields();
    },
    backButtonRoute() {
      if (this.baseView.query && this.baseView.query.from) {
        return this.baseView.query.from;
      } else return this.goBackRoute;
    },
    getHeaderToolbar() {
      return {
        screenName: "Batch Payment",
        pdf: !this.isCreateMode(),
        // audit: !this.isCreateMode(),
        audit: false,
        status: !this.isCreateMode(),
        notes: false,
        save:
          this.baseView.query.Status == "Open" &&
          this.baseView.mode == "review",
        editURL: `/supplier-batch-payment`,
        editCallBack: () => {
          this.editCallBack();
        },

        saveOptions: {
          items: this.getSaveOptions,
          keyExpr: "id",
          displayExpr: "text",
          dropDownOptions: {
            width: "150px",
          },
          saveKey: "SaveAs",
        },
        elipsis: {
          menuItems: [
            {
              menuText: "Cancel",
              disabled: false,
            },
          ],
          visible: this.isReviewMode() && this.baseView.query.Status == "A",

          clickAction: (e) => {
            if (e.itemData.menuText == "Cancel") {
              //   this.cancelPayment();
            }
          },
        },
        // saveCallback: this.saveBatchPayment,
        customButtons: [
          {
            icon: "plus",
            label: "Add New",
            route: "/call-logs/create",
            clickAction: {},
            buttonLocation: "right",
            // visible: FeatureStorage.checkPermission("CallLogs", "create"),
          },
        ],
      };
    },
    getSaveOptions() {
      var columns;
      var hasCommitAccess = FeatureStorage.checkPermission(
        "BatchPaymentCommit",
        "create"
      );

      var hasSaveAccess = FeatureStorage.checkPermission(
        "BatchPaymentSave",
        "create"
      );

      if (this.isCreateMode()) {
        columns = this.getCreateModeSaveOptions();
      } else if (this.isEditMode()) {
        columns = this.getEditModeSaveOptions();
      } else {
        columns = this.getReviewModeSaveOptions();
      }
      if (!hasCommitAccess) {
        columns = columns.filter((column) => column.text !== "Commit");
      }
      if (!hasSaveAccess) {
        columns = columns.filter((column) => column.text !== "Save as Draft");
      }

      return columns;
    },
    getScreenTitle() {
      if (this.isCreateMode()) {
        return "Create Batch Payment Run";
      } else {
        return `Batch Payment Run #${this.baseView.id}`;
      }
    },
    // Footer
    getFooterItems() {
      return [
        {
          screenField: "AllocatedAmount",
          location: "left",
          value: 0,
          label: "Suppliers Included",
          customCalc: () => {
            let selectedSups = this.selectedGridItems.map(
              (x) => x.SupplierCode
            );
            const uniqueArray = selectedSups.filter((value, index, self) => {
              return self.indexOf(value) === index;
            });
            return uniqueArray.length;
          },
          format: "Decimal-16-0",
        },
        {
          screenField: "AllocatedAmount",
          location: "left",
          value: 0,
          label: "Invoices Included",
          customCalc: (headerData, gridItems) => {
            return this.getCountSelectedInvoices(headerData, gridItems);
          },
          format: "Decimal-16-0",
        },
        {
          screenField: "FXDiscount",
          dataField: "Discount",
          location: "right",
          label: "Discount Taken",
          value: 0,
          allowEditing: false,
          customCalc: () => {
            var total = 0;
            this.selectedGridItems.forEach((element) => {
              if (element.DiscountAmount)
                total = total + element.DiscountAmount;
            });
            return total;
          },
        },
        {
          screenField: "AllocatedAmount",
          location: "right",
          value: 0,
          label: "Allocated",
          customCalc: (headerData, gridItems) => {
            return this.getTotalAllocatedAmount(headerData, gridItems);
          },
          format: "Decimal-16-2C",
        },
      ];
    },

    footerLeftItems() {
      var leftItems = this.footerItems.filter((x) => x.location == "left");

      return leftItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };
          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;
          if (c.fieldType != undefined) globalField.fieldType = c.fieldType;
          if (c.length != undefined) globalField.length = c.length;
          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },
    footerRightItems() {
      var rightItems = this.footerItems.filter(
        (x) =>
          x.location == "right" &&
          (x.hasOwnProperty("visible") ? x.visible : true)
      );

      return rightItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };

          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;

          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },
    // Filter Panel
    filterPanelTabOptions() {
      return {
        dataSource: [
          {
            id: 0,
            text: "Filters",
          },
        ],
        selectedIndex: 0,
      };
    },

    filterFormItems() {
      return [
        {
          screenField: "Date",
          filterDataField: "InvDate",
          filterLabel: "Invoice Date",
        },
        {
          screenField: "Supplier",
          filterDataField: "SupplierCode",
        },
      ];
    },
    // Tabs
    getTabs() {
      if (this.baseView.mode == "create") {
        return [
          {
            text: "Details",
            path: `/supplier-batch-payment/create`,
            applicationFeatureCode: "BatchPaymentSave",
          },
          {
            text: "Remittance Distribution",
            // path: ``,
            // applicationFeatureCode: "",
            disabled: true,
          },
        ];
      } else {
        return [
          {
            text: "Details",
            path:
              `/supplier-batch-payment/` +
              this.baseView.mode +
              "/" +
              this.baseView.id,
            applicationFeatureCode: "BatchPaymentSave",
          },
          {
            text: "Remittance Distribution",
            path: `/remittance-distribution/review/` + this.baseView.id,
            applicationFeatureCode: "BatchPaymentSave",
            disabled:
              this.baseView.mode != "review" || this.dataModel.Status != "C",
          },
        ];
      }
    },
  },
};
</script>

<style lang="scss">
.batch-payment {
  .btn-generate {
    text-align: end;
    > div {
      padding-top: 6px;
      text-align: end;
    }
  }

  .dx-button[aria-label="Draft"] {
    border: 1px solid #a2a2a2;
    .dx-button-content .dx-icon::before {
      color: #000000 !important;
    }
  }

  .dx-button[aria-label="Cancel"],
  .dx-button[aria-label="Reset"] {
    border: 1px solid #ff0000;
    color: red;
    .dx-button-content .dx-icon::before {
      color: #ff0000 !important;
    }
  }

  .dx-button[aria-label="Cancel"].dx-state-hover,
  .dx-button[aria-label="Reset"].dx-state-hover {
    background-color: #ff000036;
    color: red;
  }

  .dx-button[aria-label="Commit"] {
    background-color: #03a9f4;
    color: #fff !important;
    .dx-button-content .dx-icon::before {
      color: #fff !important;
    }
  }

  .dx-button[aria-label="Commit"].dx-state-hover {
    background-color: #028bc9;
  }

  .gc-header-form .header-form {
    margin-bottom: unset !important;

    .dx-tagbox:not(.dx-texteditor-empty):not(.dx-editor-filled):not(
        .dx-editor-outlined
      )::after {
      border-bottom: 1px solid rgba(0, 0, 0, 0.42);
    }

    .dx-tag {
      .dx-tag-content span {
        font-size: 12px;
      }
    }
  }

  .gc-details-grid {
    .dx-treelist-header-panel {
      height: 40px;
      .dx-toolbar-before,
      .dx-toolbar-after {
        height: 40px;
      }

      .dx-button[aria-label="Expand"],
      .dx-button[aria-label="Collapse"] {
        height: 22px;
        .dx-button-content {
          padding: unset !important;
          display: flex;
          -webkit-box-pack: center;
          -ms-flex-pack: center;
          justify-content: center;
          -ms-flex-line-pack: center;
          align-content: center;

          .dx-icon {
            font-size: 12px;
            margin-right: 0;
            padding: 0;
            margin-top: 2px;
          }

          .dx-button-text {
            display: inline-flex;
            -webkit-box-align: center;
            -ms-flex-align: center;
            align-items: center;
            padding: 5px 5px 5px 0px;
            font-size: 10px;
          }
        }
      }
    }
    .dx-button[aria-label="Expand"],
    .dx-button[aria-label="Collapse"] {
      background-color: #03a9f4;
      color: #fff !important;
      .dx-button-content .dx-icon::before {
        color: #fff !important;
      }
    }

    .gc-editable-field:not([role="columnheader"]) {
      .dx-texteditor.dx-editor-underlined:not(.dx-state-disabled):after {
        content: "";
        display: block;
        border-bottom: 1px solid #0000006b;
        margin-top: -1px; /* Adjust this value to position the underline properly */
      }

      .dx-texteditor.dx-editor-underlined.dx-state-disabled:not(
          .parent-cell-element
        ):after {
        content: "";
        display: block;
        border-bottom: 1px dotted #0000006b;
        margin-top: -1px; /* Adjust this value to position the underline properly */
      }

      .dx-texteditor.dx-editor-underlined.dx-state-disabled:not(
          .parent-cell-element
        ) {
        opacity: 1 !important;
      }
    }

    .disabled-cell {
      padding-left: 11px;
      padding-right: 11px;
      font-weight: bold;
    }
  }
}
</style>
